import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useLogout } from './useLogout';
import { getEnvVariables } from '../env';

enum UserActivityStatusEnum {
  'Active' = 'Active',
  'Prompted' = 'Prompted',
  'Idle' = 'Idle',
}

const secondsToMinutesAndSeconds: (seconds: number) => string = seconds => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes ? minutes + ' min.' : ''} ${seconds % 60} sec.`;
};

export const useUserActivityTracker = () => {
  const { USER_LOGOUT_NOTIFICATION_TIMER, USER_LOGOUT_TIMER } =
    getEnvVariables();
  useEffect(() => {
    if (!USER_LOGOUT_TIMER || !USER_LOGOUT_NOTIFICATION_TIMER) {
      console.info('No logout timer');
    }
  }, []);

  const { logout } = useLogout();

  const userLogoutTimer = Number(USER_LOGOUT_TIMER) || 99000000;
  const userLogoutNotificationTimer = Number(USER_LOGOUT_NOTIFICATION_TIMER) || 98000000;
  const [state, setState] = useState<UserActivityStatusEnum>(
    UserActivityStatusEnum.Active
  );
  const [remaining, setRemaining] = useState<number>(userLogoutTimer);

  const onIdle = () => {
    setState(UserActivityStatusEnum.Idle);
    logout();
  };

  const onActive = () => {
    setState(UserActivityStatusEnum.Active);
  };

  const onPrompt = () => {
    setState(UserActivityStatusEnum.Prompted);
  };

  const { getRemainingTime, activate, pause, start } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    crossTab: true,
    timeout: userLogoutTimer,
    promptBeforeIdle: userLogoutNotificationTimer,
    throttle: 500,
    startManually: !USER_LOGOUT_TIMER || !USER_LOGOUT_NOTIFICATION_TIMER,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const handleStillHere = () => {
    setState(UserActivityStatusEnum.Active);
    activate();
  };

  const timeTillPrompt = secondsToMinutesAndSeconds(
    Math.max(remaining - USER_LOGOUT_NOTIFICATION_TIMER / 1000, 0)
  );

  const timeTillLogout = secondsToMinutesAndSeconds(remaining);

  return {
    handleStillHere,
    start,
    pause,
    timeTillLogout,
    timeTillPrompt,
    prompted: state === UserActivityStatusEnum.Prompted,
  };
};
